<template>
  <div class="avatar-custom-card" ref="containerRef">
    <div class="card-header">
      <h3 class="card-title">{{ title }}</h3>
      <p v-if="desc" class="card-desc">{{ desc }}</p>
    </div>

    <div class="tabs-container">
      <div class="tab-content">
        <div class="tab-pane">
          <!-- @click="togglePlay" -->
          <div class="video-container group" @click="handleVideoContainerTap">
            <video
              v-if="shouldRenderVideo"
              ref="videoRef"
              :src="currentVideoUrl"
              :poster="currentPosterUrl"
              :muted="videoMuted || !sound"
              :loop="true"
              :controls="false"
              playsinline
              webkit-playsinline
              x5-playsinline="true"
              preload="none"
              class="video-player"
              @loadedmetadata="onVideoLoaded"
              @play="onVideoPlay"
              @pause="onVideoPause"
            >
              您的浏览器不支持视频播放
            </video>
            <img
              v-else-if="currentPosterUrl"
              :src="currentPosterUrl"
              :alt="currentTab?.title || title"
              class="video-player"
              loading="lazy"
            />

            <!-- 静音按钮 -->
            <button
              v-if="sound && shouldRenderVideo"
              class="mute-button"
              @click.stop="toggleMute"
              :title="videoMuted ? '开启声音' : '静音'"
            >
              <Muted class="text-[48px]" v-if="videoMuted" />
              <Unmuted class="text-[48px]" v-else />
            </button>

            <PlayBtn
              v-if="isMobile && currentTab?.videoUrl && showPlayControl"
              class="play-control-btn"
              :is-playing="isPlaying"
              @click.stop="togglePlay"
            />
          </div>
        </div>
      </div>
      <slot name="button-group">
        <div class="tab-nav">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            :class="['tab-button', { active: activeTab === tab.id }]"
            @click="setActiveTab(tab.id)"
          >
            {{ tab.title }}
          </button>
        </div>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useBreakpoint } from '@rendora/design/index';
import PlayBtn from '@/design/components/buttons/play-btn.vue';
import Muted from '~/assets/svg/icon-play-muted.svg?component';
import Unmuted from '~/assets/svg/icon-play-unmuted.svg?component';

interface TabItem {
  id: string;
  title: string;
  desc?: string;
  videoUrl?: string;
  image?: string;
}

const props = withDefaults(
  defineProps<{
    tabs?: Array<TabItem>;
    title: string;
    defaultActiveTab?: string;
    sound?: boolean;
    desc?: string;
    muted?: boolean;
  }>(),
  {
    tabs: () => [],
    defaultActiveTab: '',
    sound: false,
  },
);

const activeTab = ref<string>('');
const videoRef = ref<HTMLVideoElement | null>(null);
const videoMuted = ref<boolean>(true);
const isPlaying = ref(false);
const showPlayControl = ref(true);
const hasMobilePlaybackStarted = ref(false);
const containerRef = ref<HTMLElement>();
const isInViewport = ref(false);
const intersectionObserver = ref<IntersectionObserver>();
const headers = useRequestHeaders();
const { isMobile } = useBreakpoint(headers?.['user-agent']);
let hidePlayControlTimer: ReturnType<typeof setTimeout> | null = null;

// 获取当前激活的 tab 对象
const currentTab = computed(() => props.tabs.find(tab => tab.id === activeTab.value));
const shouldRenderVideo = computed(() => {
  if (!currentTab.value?.videoUrl) return false;
  if (!isMobile.value) return isInViewport.value;
  return isInViewport.value && hasMobilePlaybackStarted.value;
});

// 计算当前视频 URL
const currentVideoUrl = computed(() => {
  return isInViewport.value && currentTab.value ? currentTab.value.videoUrl : undefined;
});
// 计算当前封面图 URL
const currentPosterUrl = computed(() => {
  return currentTab.value?.image;
});

// 检查是否在视口内并自动播放
const checkAndAutoPlay = async () => {
  if (isMobile.value && !hasMobilePlaybackStarted.value) return;
  if (!isInViewport.value || !currentVideoUrl.value) return;

  await nextTick();

  const video = videoRef.value;
  if (!video) return;

  try {
    video.load();
    await video.play();
  } catch {
    // Ignore autoplay rejection on mobile browsers.
  }
};

const clearHidePlayControlTimer = () => {
  if (hidePlayControlTimer) {
    clearTimeout(hidePlayControlTimer);
    hidePlayControlTimer = null;
  }
};

const scheduleHidePlayControl = () => {
  if (!isMobile.value || !isPlaying.value) return;

  clearHidePlayControlTimer();
  hidePlayControlTimer = setTimeout(() => {
    showPlayControl.value = false;
  }, 1500);
};

const pauseVideo = () => {
  clearHidePlayControlTimer();
  if (videoRef.value) {
    if (!videoRef.value.paused) {
      videoRef.value.pause();
    }

    if (videoRef.value.readyState > 0) {
      videoRef.value.currentTime = 0;
    }
  }
  isPlaying.value = false;
  showPlayControl.value = true;
};

const resetMobilePlayback = () => {
  if (!isMobile.value) return;
  hasMobilePlaybackStarted.value = false;
};

const setActiveTab = async (tabId: string) => {
  if (activeTab.value === tabId) return;

  pauseVideo();
  resetMobilePlayback();
  activeTab.value = tabId;

  // 等待DOM更新后自动播放新的视频
  await nextTick();
  checkAndAutoPlay();
};

const toggleMute = () => {
  videoMuted.value = !videoMuted.value;
};

const togglePlay = async () => {
  showPlayControl.value = true;

  if (isMobile.value && !shouldRenderVideo.value) {
    hasMobilePlaybackStarted.value = true;
    await nextTick();
  }

  const video = videoRef.value;
  if (!video) return;

  if (video.paused) {
    try {
      await video.play();
      scheduleHidePlayControl();
    } catch {
      // Ignore play rejection on mobile browsers.
    }
  } else {
    video.pause();
  }
};

const handleVideoContainerTap = () => {
  if (!isMobile.value) return;

  togglePlay();
};

const onVideoPlay = () => {
  isPlaying.value = true;
  showPlayControl.value = true;
  scheduleHidePlayControl();
};

const onVideoPause = () => {
  isPlaying.value = false;
  clearHidePlayControlTimer();
  showPlayControl.value = true;
};

const onVideoLoaded = () => {
  // const video = event.target as HTMLVideoElement;
  // console.log('视频加载完成:', video.duration)
};

// 设置 Intersection Observer
const setupIntersectionObserver = () => {
  if (!containerRef.value || typeof IntersectionObserver === 'undefined') return;

  intersectionObserver.value = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        isInViewport.value = entry.isIntersecting;

        if (entry.isIntersecting) {
          if (!isMobile.value) {
            // 进入视口时自动播放
            // 等待 DOM 更新（src 被赋值）后再播放
            nextTick(() => {
              checkAndAutoPlay();
            });
          }
        } else {
          // 离开视口时暂停视频
          pauseVideo();
          resetMobilePlayback();
        }
      });
    },
    {
      threshold: 0.3, // 当30%的元素可见时触发
    },
  );

  intersectionObserver.value.observe(containerRef.value);
};

// 初始化默认选中的tab
onMounted(() => {
  if (props.defaultActiveTab && props.tabs.some(tab => tab.id === props.defaultActiveTab)) {
    activeTab.value = props.defaultActiveTab;
  } else if (props.tabs.length > 0) {
    activeTab.value = props.tabs[0].id;
  }

  // 设置视口观察器
  nextTick(() => {
    setupIntersectionObserver();
  });
});

// 组件卸载时清理观察器
onUnmounted(() => {
  clearHidePlayControlTimer();
  if (intersectionObserver.value) {
    intersectionObserver.value.disconnect();
  }
});

// 监听tabs变化，更新activeTab
watch(
  () => props.tabs,
  newTabs => {
    if (newTabs.length > 0 && !newTabs.some(tab => tab.id === activeTab.value)) {
      activeTab.value = newTabs[0].id;
      resetMobilePlayback();
      // 新tabs加载后检查是否需要自动播放
      nextTick(() => {
        checkAndAutoPlay();
      });
    }
  },
  { immediate: true },
);

// 监听muted属性变化
watch(
  () => props.muted,
  newMuted => {
    videoMuted.value = newMuted;
  },
);
</script>

<style scoped lang="scss" src="./pc.scss" />
<style scoped lang="scss" src="./mobile.scss" />
